import { useEffect, useRef, useState } from 'react';

import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  Icon,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@/ui';

import CoachSessionParticipants from './CoachSessionParticipants';
import { formatIsoDateTimeRange, formatIsoTime } from './calendarGrid';
import { cancelFailureMessage } from './cancelFailureMessage';
import useHideCometChat from './useHideCometChat';

import type { CalendarActionSubject } from '@/features/appointments/calendarAppointmentProjection';
import type {
  AppointmentCancellationInitiator,
  BrowserAppointmentView,
  ParticipantAppointment,
} from '@/features/appointments/types';
import type {
  AdminLifecycleCommand,
  PlatformAdminLifecycleGuardOptions,
} from '@/features/appointments/usePlatformAdminAppointmentLifecycleGuard';
import type {
  CoachCancelSessionInitiator,
  CoachCancelSessionOptionsResponse,
  CoachCancelSessionResponse,
  CoachCancelSessionScope,
  CoachCancelSessionScopeOption,
} from '@/store/api/sessionApi';
import type { Session } from '@/types';
import type { FC, FormEvent } from 'react';

import {
  APPOINTMENT_RETRY_BUTTON_LABEL,
  appointmentPendingMessage,
  appointmentRestoredSubmittingMessage,
} from '@/features/appointments/appointmentStatusCopy';
import { COACH_CANCEL_APPOINTMENT_CAPABILITY } from '@/features/appointments/browserCapability';
import { shouldReconcileParticipantAppointmentWrite } from '@/features/appointments/participantAppointmentError';
import {
  cadenceFromOccurrences,
  formatSeriesSummary,
} from '@/features/appointments/seriesSummary';
import {
  appointmentSeriesCommandFields,
  appointmentUpgradeGuardSpent,
  canonicalAppointmentCommand,
  storedAppointmentLifecycleTransport,
  useAppointmentLifecycleGuard,
} from '@/features/appointments/useAppointmentLifecycleGuard';
import {
  PLATFORM_ADMIN_MANUAL_REVIEW_MESSAGE,
  PLATFORM_ADMIN_TERMINAL_HYDRATION_FORBIDDEN_MESSAGE,
  usePlatformAdminAppointmentLifecycleGuard,
} from '@/features/appointments/usePlatformAdminAppointmentLifecycleGuard';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  shouldReconcileAppointmentWrite,
  useCancelAppointmentMutation,
  useGetAppointmentsQuery,
  useGetCoachCancelAppointmentOperationQuery,
  useLazyGetAppointmentQuery,
} from '@/store/api/appointmentApi';
import {
  useCancelMomentsParticipantAppointmentAsCoachMutation,
  useGetMomentsParticipantAppointmentOperationQuery,
  useLazyGetMomentsParticipantAppointmentQuery,
  useParticipantAppointmentRouteDecision,
} from '@/store/api/momentsParticipantAppointmentApi';
import {
  useAdminCancelSessionMutation,
  useAdminChemistryCancelSessionMutation,
  useAdminGetSessionCancelOptionsQuery,
  useCoachCancelSessionMutation,
  useCoachCancelSessionOptionsQuery,
} from '@/store/api/sessionApi';

interface CoachCancelSessionDialogProps {
  actionSubject?: CalendarActionSubject;
  adminCoachId?: string;
  close: () => void;
  lifecycleOptions?: PlatformAdminLifecycleGuardOptions;
  participantAppointmentTransport?: 'moments' | 'rails';
  session: Session;
  timeZone?: string | null;
}

const SESSION_INITIATOR_OPTIONS: Array<{
  label: string;
  value: CoachCancelSessionInitiator;
}> = [
  { label: 'Member', value: 'member' },
  { label: 'Coach', value: 'coach' },
];

const ADMIN_APPOINTMENT_INITIATOR_OPTIONS: Array<{
  label: string;
  value: AppointmentCancellationInitiator;
}> = [
  { label: 'Member', value: 'member' },
  { label: 'Coach', value: 'coach' },
  { label: 'Ops', value: 'admin' },
];

// The persisted native intent feeds only the coach-native path, whose command
// initiator is two-valued. Widening it to 'admin' would remove a guard and make
// the CoachCancelSessionInitiator cast on the native submit path falsifiable.
interface PersistedNativeCancellationIntent {
  initiator: CoachCancelSessionInitiator;
  nearTermAcknowledged: boolean;
  opsNote: string;
  scope: 'appointment' | 'this_and_following';
}

const persistedNativeCancellationIntent = (
  bytes: string | undefined,
  appointmentId: string | null | undefined,
): PersistedNativeCancellationIntent | null => {
  if (!bytes || !appointmentId) return null;
  try {
    const value = JSON.parse(bytes) as Record<string, unknown>;
    if (
      value.appointmentId !== appointmentId ||
      value.contractVersion !== 'appointment-coach-cancel-browser.v2' ||
      (value.initiator !== 'coach' && value.initiator !== 'member') ||
      typeof value.nearTermAcknowledged !== 'boolean' ||
      (value.opsNote !== null && typeof value.opsNote !== 'string') ||
      (value.scope !== 'appointment' && value.scope !== 'this_and_following')
    ) {
      return null;
    }
    return {
      initiator: value.initiator,
      nearTermAcknowledged: value.nearTermAcknowledged,
      opsNote: value.opsNote ?? '',
      scope: value.scope,
    };
  } catch {
    return null;
  }
};

const cancellationResponseFromAppointments = (
  appointments: Array<BrowserAppointmentView | ParticipantAppointment>,
): CoachCancelSessionResponse => {
  const schedules = appointments.flatMap((appointment) =>
    Object.hasOwn(appointment, 'currentSchedule')
      ? [appointment.currentSchedule]
      : [],
  );
  const starts = schedules.map(({ startAt }) => startAt);
  const ends = schedules.map(({ endAt }) => endAt);
  return {
    cancelled: appointments.length,
    windowEnd: ends.sort().at(-1),
    windowStart: starts.sort().at(0),
  };
};

const countFromResponse = (
  response?: CoachCancelSessionResponse,
): number | undefined =>
  response?.cancelled ??
  response?.cancelledSessionsCount ??
  response?.cancelled_sessions_count;

const sessionCountLabel = (count: number): string =>
  `${count} ${count === 1 ? 'session' : 'sessions'}`;

const windowStartFromOption = (
  option?: CoachCancelSessionScopeOption,
): string | null | undefined => option?.windowStart ?? option?.window_start;

const windowEndFromOption = (
  option?: CoachCancelSessionScopeOption,
): string | null | undefined => option?.windowEnd ?? option?.window_end;

const windowStartFromResponse = (
  response?: CoachCancelSessionResponse,
): string | null | undefined => response?.windowStart ?? response?.window_start;

const windowEndFromResponse = (
  response?: CoachCancelSessionResponse,
): string | null | undefined => response?.windowEnd ?? response?.window_end;

const terminalSummaryFromResponse = (response: CoachCancelSessionResponse) => ({
  affectedCount: countFromResponse(response) ?? 1,
  windowEnd: windowEndFromResponse(response) ?? null,
  windowStart: windowStartFromResponse(response) ?? null,
});

const formatIsoDate = (iso: string, timeZone?: string | null): string => {
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    weekday: 'long',
  };
  if (timeZone) options.timeZone = timeZone;
  return new Date(iso).toLocaleDateString('en', options);
};

const formatSeriesWindow = (
  startedAt: string,
  endedAt: string,
  timeZone?: string | null,
): string => {
  const startDate = formatIsoDate(startedAt, timeZone);
  const endDate = formatIsoDate(endedAt, timeZone);
  if (startDate === endDate) {
    return formatIsoDateTimeRange(startedAt, endedAt, timeZone);
  }

  return `${startDate} • ${formatIsoTime(
    startedAt,
    timeZone,
  )} – ${endDate} • ${formatIsoTime(endedAt, timeZone)}`;
};

const scopeOption = (
  options: CoachCancelSessionOptionsResponse | undefined,
  scope: CoachCancelSessionScope,
) => options?.scopes.find((option) => option.scope === scope);

const adminCancelOptionsErrorMessage = (error: unknown): string => {
  const message = (error as { data?: { error?: unknown } } | undefined)?.data
    ?.error;
  return typeof message === 'string' && message
    ? `Cancellation options couldn't be loaded for this session: ${message}`
    : "Cancellation options couldn't be loaded for this session. The coach may not be on the new Mento calendar. Nothing was cancelled.";
};

const CoachCancelSessionDialog: FC<CoachCancelSessionDialogProps> = ({
  actionSubject,
  adminCoachId,
  close,
  lifecycleOptions,
  participantAppointmentTransport,
  session,
  timeZone,
}) => {
  useHideCometChat();
  const { user: currentUser } = useCurrentUser();
  const [scope, setScope] = useState<CoachCancelSessionScope>('single');
  const [initiatedBy, setInitiatedBy] = useState<
    AppointmentCancellationInitiator | ''
  >('');
  const [opsNote, setOpsNote] = useState('');
  const [reason, setReason] = useState('');
  const [confirmedWithin48Hours, setConfirmedWithin48Hours] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const [authoritativeStartAt, setAuthoritativeStartAt] = useState<
    string | null
  >(null);
  const [error, setError] = useState('');
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const submitInFlightRef = useRef(false);
  const restoredPendingAppliedRef = useRef(false);
  // MEN-4122: Ops authority comes from `adminCoachId`, never from the subject
  // tag alone. `platform-admin-appointment` records where the projection came
  // from — `useCalendarWindow` and `ListView` build every native Appointment
  // that way, including the coach's own — so reading it without `adminCoachId`
  // showed a coach the Ops cancellation surface and routed their Confirm
  // through the Ops selected-pair lifecycle command instead of the coach
  // cancel. `useSessionActions.actionsFor` already pairs the tag with
  // `adminCoachId`; this matches it.
  const adminAppointment =
    adminCoachId && actionSubject?.source === 'platform-admin-appointment'
      ? actionSubject.authoritative
      : null;
  const nativeAppointmentId = adminCoachId
    ? adminAppointment?.id
    : session.sourceAppointmentId;
  const coachNativeAppointment = Boolean(
    nativeAppointmentId && !adminAppointment,
  );
  const nativeOwnerKey =
    nativeAppointmentId && !adminAppointment
      ? `coach-cancel:${adminCoachId ?? 'coach'}:${session.id}:${nativeAppointmentId}`
      : undefined;
  const persistedTransport = storedAppointmentLifecycleTransport(
    nativeOwnerKey,
    'rails',
  );
  const [establishedRoute, setEstablishedRoute] = useState<{
    appointmentId: string;
    transport: 'moments';
  }>();
  const establishedTransport =
    establishedRoute && establishedRoute.appointmentId === nativeAppointmentId
      ? establishedRoute.transport
      : undefined;
  const routeDecision = useParticipantAppointmentRouteDecision({
    coachExternalId:
      coachNativeAppointment && !participantAppointmentTransport
        ? currentUser.id
        : undefined,
    establishedTransport:
      persistedTransport ??
      participantAppointmentTransport ??
      establishedTransport,
    flow: 'cancellation',
  });
  const nativeTransport =
    persistedTransport ??
    participantAppointmentTransport ??
    establishedTransport ??
    (routeDecision.state === 'ready' ? routeDecision.transport : undefined);
  const useDirectParticipantTransport =
    coachNativeAppointment && nativeTransport === 'moments';
  const nativeRouteBlocked = Boolean(
    coachNativeAppointment &&
    !participantAppointmentTransport &&
    !establishedTransport &&
    routeDecision.state !== 'ready',
  );
  useEffect(() => {
    if (
      coachNativeAppointment &&
      !participantAppointmentTransport &&
      routeDecision.state === 'ready' &&
      routeDecision.transport === 'moments'
    ) {
      setEstablishedRoute({
        appointmentId: nativeAppointmentId!,
        transport: 'moments',
      });
    }
  }, [coachNativeAppointment, participantAppointmentTransport, routeDecision]);
  const initiatorOptions = adminAppointment
    ? ADMIN_APPOINTMENT_INITIATOR_OPTIONS
    : SESSION_INITIATOR_OPTIONS;
  // MEN-5338: Rails' Ops Session series cancel
  // (Sessions::BookingSeriesCancellation) rejects a blank note_to_ops with 422.
  // Admin chemistry already requires its note, sent as `reason`.
  const adminSessionNoteRequired = Boolean(
    adminCoachId && !nativeAppointmentId && !session.isChemistryCall,
  );
  const reasonRequired =
    !coachNativeAppointment &&
    (Boolean(session.isChemistryCall) ||
      Boolean(session.sourceAppointmentCancelReasonRequired));
  const displayedNativeAppointmentStartAt =
    actionSubject?.source !== 'session' &&
    actionSubject?.authoritative.currentSchedule
      ? actionSubject.authoritative.currentSchedule.startAt
      : session.startedAt;
  const nativeAppointmentStartAt =
    authoritativeStartAt ?? displayedNativeAppointmentStartAt;
  let opsNoteLabel = 'Notes for Mento operations (optional)';
  if (adminAppointment || adminSessionNoteRequired)
    opsNoteLabel = 'Notes for Mento operations';
  else if (reasonRequired) opsNoteLabel = 'Cancellation reason';
  const adminMemberCollision = Boolean(
    adminAppointment && currentUser.id === adminAppointment.member.id,
  );
  const [pendingOperationId, setPendingOperationId] = useState<string | null>(
    null,
  );
  const [
    pendingOperationRetryAfterSeconds,
    setPendingOperationRetryAfterSeconds,
  ] = useState<number | null>(null);
  const [confirmation, setConfirmation] =
    useState<CoachCancelSessionResponse | null>(null);
  const [operationReviewRequired, setOperationReviewRequired] = useState(false);
  const [coachCancelSession, coachMutation] = useCoachCancelSessionMutation();
  const [cancelAppointment, appointmentMutation] =
    useCancelAppointmentMutation();
  const [cancelParticipantAppointment, participantAppointmentMutation] =
    useCancelMomentsParticipantAppointmentAsCoachMutation();
  const [refreshAppointment] = useLazyGetAppointmentQuery();
  const [refreshParticipantAppointment] =
    useLazyGetMomentsParticipantAppointmentQuery();
  const appointmentCapability = COACH_CANCEL_APPOINTMENT_CAPABILITY;
  const lifecycle = useAppointmentLifecycleGuard({
    capability: appointmentCapability,
    displayed: {
      id: nativeAppointmentId,
      seriesId: session.sourceAppointmentSeriesId,
      seriesMutationVersion: session.sourceAppointmentSeriesMutationVersion,
      version: session.sourceAppointmentVersion,
    },
    legacyTransport: 'rails',
    ownerKey: nativeOwnerKey,
    readAppointment: async () => {
      const request = useDirectParticipantTransport
        ? refreshParticipantAppointment({
            appointmentId: nativeAppointmentId ?? '',
          })
        : refreshAppointment({ appointmentId: nativeAppointmentId ?? '' });
      return request.unwrap().finally(() => request.unsubscribe());
    },
    transport: nativeTransport,
  });
  const {
    clearOwnedOperation,
    consumeConfirmedReceipt,
    currentCommand,
    recordConfirmed,
    rereadAfterRejection,
  } = lifecycle;
  const persistedNativeIntent = coachNativeAppointment
    ? persistedNativeCancellationIntent(
        currentCommand()?.bytes,
        nativeAppointmentId,
      )
    : null;
  const nativeCommandFrozen = Boolean(
    coachNativeAppointment &&
    (lifecycle.restoredSubmitting ||
      lifecycle.restoredPending ||
      lifecycle.needsReconciliation ||
      pendingOperationId ||
      operationReviewRequired),
  );
  const restoredSubmittingNeedsRetry = Boolean(
    lifecycle.restoredSubmitting && !submitInFlightRef.current,
  );
  const [adminIdempotencyKey, setAdminIdempotencyKey] = useState(() =>
    window.crypto.randomUUID(),
  );
  const adminCommand: AdminLifecycleCommand = {
    appointmentId: adminAppointment?.id ?? '',
    coachId: adminAppointment?.coach.id ?? adminCoachId ?? '',
    command: 'cancel',
    idempotencyKey: adminIdempotencyKey,
    kind: adminAppointment?.kind ?? 'coaching',
    memberId: adminAppointment?.member.id ?? '',
    payload: {
      expectedSeriesMutationVersion:
        adminAppointment?.seriesId === null
          ? null
          : (adminAppointment?.seriesMutationVersion ?? null),
      expectedVersion: adminAppointment?.version ?? 0,
      initiator: initiatedBy || 'coach',
      nearTermChemistryConfirmed: confirmedWithin48Hours,
      opsNote: opsNote.trim(),
      reason: reason.trim() || null,
      scope:
        scope === 'this_and_following' ? 'this_and_following' : 'appointment',
    },
    presentation: { memberEmail: session.user.email },
  };
  const adminLifecycle = usePlatformAdminAppointmentLifecycleGuard(
    adminCommand,
    {
      ...lifecycleOptions,
      enabled: Boolean(adminAppointment),
    },
  );
  const adminNonterminal =
    adminLifecycle.record?.phase === 'submitting' ||
    adminLifecycle.record?.phase === 'ambiguous' ||
    adminLifecycle.record?.phase === 'pending';
  const adminRejected = adminLifecycle.record?.phase === 'rejected';
  const adminManualReview =
    adminLifecycle.record?.phase === 'needs_reconciliation';
  const adminCommandFrozen =
    adminNonterminal || adminManualReview || adminRejected;
  const actionControlsFrozen = adminCommandFrozen || nativeCommandFrozen;
  const adminAmbiguous = adminLifecycle.record?.phase === 'ambiguous';
  useEffect(() => {
    if (!adminLifecycle.record || adminLifecycle.command.command !== 'cancel')
      return;
    const stored = adminLifecycle.command.payload;
    setScope(
      stored.scope === 'this_and_following' ? 'this_and_following' : 'single',
    );
    setInitiatedBy(stored.initiator);
    setConfirmedWithin48Hours(stored.nearTermChemistryConfirmed);
    setOpsNote(stored.opsNote);
    setReason(stored.reason ?? '');
  }, [adminLifecycle.command, adminLifecycle.record]);
  useEffect(() => {
    if (!persistedNativeIntent) return;
    setScope(
      persistedNativeIntent.scope === 'this_and_following'
        ? 'this_and_following'
        : 'single',
    );
    setInitiatedBy(persistedNativeIntent.initiator);
    setConfirmedWithin48Hours(persistedNativeIntent.nearTermAcknowledged);
    setOpsNote(persistedNativeIntent.opsNote);
  }, [
    persistedNativeIntent?.initiator,
    persistedNativeIntent?.nearTermAcknowledged,
    persistedNativeIntent?.opsNote,
    persistedNativeIntent?.scope,
  ]);
  const [adminCancelSession, adminMutation] = useAdminCancelSessionMutation();
  const [adminChemistryCancelSession, chemistryMutation] =
    useAdminChemistryCancelSessionMutation();
  const coachOptions = useCoachCancelSessionOptionsQuery(session.id, {
    skip: Boolean(adminCoachId) || Boolean(nativeAppointmentId),
  });
  const adminOptions = useAdminGetSessionCancelOptionsQuery(
    {
      coachId: adminCoachId ?? '',
      id: session.id,
      memberId: session.userId,
    },
    {
      skip:
        !adminCoachId ||
        session.isChemistryCall ||
        Boolean(nativeAppointmentId),
    },
  );
  const options = adminCoachId ? adminOptions.data : coachOptions.data;
  // MEN-5338: the Ops Session route is refused when Moments does not own the
  // coach's calendar (403) or Rails rejects the Session (422). Without this the
  // drawer showed default options and only failed on submit.
  const adminOptionsError = adminOptions.isError
    ? adminCancelOptionsErrorMessage(adminOptions.error)
    : null;
  let { isLoading } = coachMutation;
  if (nativeAppointmentId) {
    isLoading = appointmentMutation.isLoading;
    if (useDirectParticipantTransport) {
      isLoading = participantAppointmentMutation.isLoading;
    }
  }
  if (adminCoachId && !nativeAppointmentId) {
    isLoading = session.isChemistryCall
      ? chemistryMutation.isLoading
      : adminMutation.isLoading;
  }

  const hasStoredAdminSeriesScope = Boolean(
    adminLifecycle.record &&
    adminLifecycle.command.command === 'cancel' &&
    adminLifecycle.command.payload.scope === 'this_and_following',
  );
  const hasStoredNativeSeriesScope =
    persistedNativeIntent?.scope === 'this_and_following';
  const hasSeriesOption =
    hasStoredAdminSeriesScope ||
    hasStoredNativeSeriesScope ||
    (nativeAppointmentId
      ? Boolean(session.sourceAppointmentSeriesId) &&
        Boolean(
          session.sourceAppointmentCancelScopes?.includes('this_and_following'),
        )
      : Boolean(session.recurringId));
  useEffect(() => {
    if (!hasSeriesOption && !actionControlsFrozen) setScope('single');
  }, [actionControlsFrozen, hasSeriesOption]);
  const isSeries = scope === 'this_and_following';
  // MEN-4306 review: `userId` is an RTK cache key and never reaches the wire,
  // so without a coach/member pair this issues `GET /appointments?series_id=`
  // alone. Rails routes that to the participant list, scopes it to the caller,
  // and Moments drops every appointment whose coach and member are both
  // someone else -- which is always true of an Ops actor. Supplying the
  // selected pair takes the admin branch instead, where the Ops viewer is the
  // admin actor rather than the participant. The coach path keeps the
  // participant branch, where the coach genuinely is a participant.
  const nativeSeriesAppointments = useGetAppointmentsQuery(
    {
      ...(adminAppointment
        ? {
            coachId: adminAppointment.coach.id,
            memberId: adminAppointment.member.id,
          }
        : {}),
      seriesId: session.sourceAppointmentSeriesId ?? undefined,
      userId: currentUser.id,
    },
    {
      // MEN-4306: gated on `nativeAppointmentId`, not `coachNativeAppointment`.
      // The latter is false whenever an adminAppointment is set, so the Ops
      // cancel skipped this query entirely, leaving the series summary with no
      // occurrences to describe -- and because both cancel-options queries are
      // also skipped for a native appointment, `seriesOption?.count` was
      // undefined too and the summary fell through to its generic label.
      //
      // And gated on `hasSeriesOption`, not `isSeries`: selecting the series
      // scope is what the summary describes, so starting the request there
      // guaranteed the summary had nothing to describe for the whole first
      // round trip -- `currentData` is undefined until it lands -- and the
      // fallback rendered at exactly the moment Ops read the line. Whether the
      // series scope is offered is known when the dialog opens, so the
      // occurrences are fetched then and the scope selection is a re-render
      // rather than a round trip.
      skip:
        !nativeAppointmentId ||
        !hasSeriesOption ||
        !session.sourceAppointmentSeriesId,
    },
  );
  const baselineOrderedSeriesAppointments =
    nativeSeriesAppointments.currentData?.appointments
      .filter(
        (appointment) =>
          appointment.seriesId === session.sourceAppointmentSeriesId,
      )
      .sort((left, right) => {
        const baselineOrder = left.baselineSchedule.startAt.localeCompare(
          right.baselineSchedule.startAt,
        );
        return baselineOrder || left.id.localeCompare(right.id);
      }) ?? [];
  const selectedBaselineIndex = baselineOrderedSeriesAppointments.findIndex(
    (appointment) => appointment.id === nativeAppointmentId,
  );
  // Every occurrence from the selected one onward, filtered or not. This is
  // what the series' recurrence rule is written in: cancelling or unbinding an
  // occurrence changes which sessions are affected, never how often the series
  // meets (MEN-4306 review).
  const recurrenceTailAppointments =
    selectedBaselineIndex < 0
      ? []
      : baselineOrderedSeriesAppointments.slice(selectedBaselineIndex);
  const authoritativeTailAppointments = recurrenceTailAppointments.filter(
    (appointment) =>
      appointment.activeProviderBinding !== null &&
      appointment.bookingStatus === 'confirmed' &&
      appointment.lifecycleStatus === 'scheduled' &&
      Date.parse(appointment.currentSchedule.startAt) > currentTime,
  );
  const nativeTailReviewUnavailable =
    coachNativeAppointment &&
    isSeries &&
    (nativeSeriesAppointments.isError ||
      (!nativeSeriesAppointments.isFetching &&
        authoritativeTailAppointments.length === 0));
  const railsOperationStatus = useGetCoachCancelAppointmentOperationQuery(
    { operationId: pendingOperationId ?? '', userId: currentUser.id },
    {
      pollingInterval: upgradeRequired
        ? 0
        : Math.min(
            5000,
            Math.max(1000, (pendingOperationRetryAfterSeconds ?? 5) * 1000),
          ),
      skip:
        !pendingOperationId ||
        upgradeRequired ||
        operationReviewRequired ||
        useDirectParticipantTransport,
    },
  );
  const participantOperationStatus =
    useGetMomentsParticipantAppointmentOperationQuery(
      { operationId: pendingOperationId ?? '' },
      {
        pollingInterval: upgradeRequired
          ? 0
          : Math.min(
              5000,
              Math.max(1000, (pendingOperationRetryAfterSeconds ?? 5) * 1000),
            ),
        skip:
          !pendingOperationId ||
          upgradeRequired ||
          operationReviewRequired ||
          !useDirectParticipantTransport,
      },
    );
  const operationStatus = useDirectParticipantTransport
    ? participantOperationStatus
    : railsOperationStatus;
  const requestClose = () => {
    if (
      pendingOperationId ||
      lifecycle.isReading ||
      submitInFlightRef.current ||
      isLoading ||
      adminLifecycle.record?.phase === 'submitting' ||
      adminLifecycle.record?.phase === 'ambiguous' ||
      adminLifecycle.record?.phase === 'pending'
    )
      return;
    close();
  };
  const startNewAdminAttempt = () => {
    adminLifecycle
      .clear()
      .then((cleared) => {
        if (!cleared) return;
        setAdminIdempotencyKey(window.crypto.randomUUID());
        setError('');
      })
      .catch(() => undefined);
  };

  useEffect(() => {
    if (lifecycle.restoredPending && !restoredPendingAppliedRef.current) {
      restoredPendingAppliedRef.current = true;
      setPendingOperationId(lifecycle.restoredPending.operationId);
      setPendingOperationRetryAfterSeconds(
        lifecycle.restoredPending.retryAfterSeconds,
      );
    }
  }, [lifecycle.restoredPending]);

  useEffect(() => {
    if (!lifecycle.needsReconciliation) return;
    setPendingOperationId(null);
    setPendingOperationRetryAfterSeconds(null);
    setOperationReviewRequired(true);
    setError(
      'The provider outcome requires reconciliation. This cancellation remains saved for Mento operations review.',
    );
  }, [lifecycle.needsReconciliation]);

  useEffect(() => {
    if (appointmentUpgradeGuardSpent(operationStatus.error)) {
      setUpgradeRequired(true);
    }
  }, [operationStatus.error]);

  useEffect(() => {
    const operation = operationStatus.currentData;
    if (!operation || operation.id !== pendingOperationId) return;
    if (operation.status === 'pending') {
      setPendingOperationRetryAfterSeconds(operation.retryAfterSeconds ?? null);
    }
    if (operation.status === 'confirmed') {
      if (!operation.appointmentIds.includes(nativeAppointmentId ?? '')) {
        setOperationReviewRequired(true);
        setPendingOperationId(null);
        setPendingOperationRetryAfterSeconds(null);
        submitInFlightRef.current = false;
        setError(
          'The confirmed operation does not match this appointment. The cancellation remains saved for review.',
        );
        return;
      }
      const response = cancellationResponseFromAppointments(
        operation.appointments,
      );
      // Best effort: `recordConfirmed` only reports whether the local
      // acknowledgement receipt persisted.
      recordConfirmed({
        retainReceipt: true,
        terminalSummary: terminalSummaryFromResponse(response),
      });
      setPendingOperationId(null);
      setPendingOperationRetryAfterSeconds(null);
      submitInFlightRef.current = false;
      // MEN-4306: the cancellation is confirmed. A failed local acknowledgement
      // receipt degrades nothing server-side, so it must not surface as an error
      // inviting a retry on a completed destructive action. Every sibling
      // surface already ignores it.
      setConfirmation(response);
    }
    if (operation.status === 'rejected') {
      setPendingOperationId(null);
      setPendingOperationRetryAfterSeconds(null);
      const command = currentCommand();
      if (command) {
        rereadAfterRejection({
          ...command,
          operationId: operation.id,
        }).catch(() => undefined);
      }
      setError(
        ('rejection' in operation ? operation.rejection?.message : undefined) ??
          'We could not cancel this session. Please try again.',
      );
    }
    if (operation.status === 'needs_reconciliation') {
      const command = currentCommand();
      if (command) {
        lifecycle.recordNeedsReconciliation({
          ...command,
          operationId: operation.id,
        });
      }
      setPendingOperationId(null);
      setPendingOperationRetryAfterSeconds(null);
      submitInFlightRef.current = false;
      setOperationReviewRequired(true);
      setError(
        'The provider outcome requires reconciliation. This cancellation remains saved for Mento operations review.',
      );
    }
  }, [
    operationStatus.currentData,
    pendingOperationId,
    rereadAfterRejection,
    recordConfirmed,
    currentCommand,
    nativeAppointmentId,
  ]);

  useEffect(() => {
    if (!adminAppointment || !adminLifecycle.result) return;
    if (adminLifecycle.result.status === 'confirmed') {
      setConfirmation({
        cancelled: adminLifecycle.result.appointments.length || 1,
      });
    } else if (adminLifecycle.result.status === 'rejected') {
      setError(
        adminLifecycle.result.rejection?.message ??
          'We could not cancel this session. Please try again.',
      );
    }
  }, [adminAppointment, adminLifecycle.result]);

  const singleOption = scopeOption(options, 'single');
  const seriesOption = scopeOption(options, 'this_and_following');
  const confirmationCount = countFromResponse(confirmation ?? undefined);
  const confirmedAffectedCount =
    confirmationCount ?? (isSeries ? undefined : 1);
  const authoritativeTailStarts = authoritativeTailAppointments
    .map((appointment) => appointment.currentSchedule.startAt)
    .sort();
  const authoritativeTailEnds = authoritativeTailAppointments
    .map((appointment) => appointment.currentSchedule.endAt)
    .sort();
  const seriesPreviewWindowStart =
    authoritativeTailStarts.at(0) ??
    windowStartFromOption(seriesOption) ??
    session.startedAt;
  const seriesPreviewWindowEnd =
    authoritativeTailEnds.at(-1) ??
    windowEndFromOption(seriesOption) ??
    session.endedAt;
  // MEN-4122 produced formatSeriesSummary for the reschedule dialog; it now
  // lives in features/appointments/seriesSummary so both dialogs share it. The
  // cancel contract carries no cadence field, but the series occurrences are
  // loaded above, so the interval is derived from them and drops out cleanly
  // when the series is irregular.
  // The cadence and its date range must be decided in the same timezone the
  // summary is rendered in, or the summary can name a weekday and print the
  // adjacent day's date (MEN-4306 review). The anchor occurrence supplies the
  // weekday and time only once cadenceFromOccurrences has confirmed every
  // occurrence agrees on them, so an unordered collection cannot mislabel it.
  const seriesCadenceTimezone =
    authoritativeTailAppointments.length > 0
      ? (timeZone ?? authoritativeTailAppointments[0]!.currentSchedule.timezone)
      : null;
  const seriesCadenceSummary =
    isSeries && seriesCadenceTimezone
      ? formatSeriesSummary(
          authoritativeTailAppointments[0]!.currentSchedule.startAt,
          authoritativeTailAppointments[0]!.currentSchedule.endAt,
          seriesCadenceTimezone,
          cadenceFromOccurrences(
            {
              // The range describes what will actually be cancelled...
              affected: authoritativeTailAppointments.map((appointment) => ({
                endAt: appointment.currentSchedule.endAt,
                startAt: appointment.currentSchedule.startAt,
              })),
              // ...while the interval describes how the series meets.
              recurrence: recurrenceTailAppointments.map((appointment) => ({
                endAt: appointment.currentSchedule.endAt,
                startAt: appointment.currentSchedule.startAt,
              })),
            },
            seriesCadenceTimezone,
          ),
        )
      : null;
  // The count and window stay -- they answer "how many am I cancelling" -- and
  // the cadence is appended when derivable, which is what MEN-4306 asks for.
  // When neither is known the line names the scope in full instead of echoing
  // the checkbox's own label back: 'This and following sessions' told Ops
  // nothing about what Confirm was about to destroy, and it read as a stuck
  // control rather than as an unknown (MEN-4306, Jake).
  const seriesOccurrenceCount =
    authoritativeTailAppointments.length || seriesOption?.count;
  const seriesScopeSummary = seriesOccurrenceCount
    ? [
        `${seriesOccurrenceCount} ${
          seriesOccurrenceCount === 1 ? 'session' : 'sessions'
        } from ${formatSeriesWindow(
          seriesPreviewWindowStart,
          seriesPreviewWindowEnd,
          timeZone,
        )}`,
        seriesCadenceSummary,
      ]
        .filter(Boolean)
        .join(' · ')
    : (seriesCadenceSummary ?? 'All future sessions in this series.');
  const seriesConfirmationWindowStart =
    windowStartFromResponse(confirmation ?? undefined) ??
    seriesPreviewWindowStart;
  const seriesConfirmationWindowEnd =
    windowEndFromResponse(confirmation ?? undefined) ?? seriesPreviewWindowEnd;
  let cancellationActor = adminCoachId ? 'Mento operations' : 'the coach';
  if (!adminCoachId && initiatedBy === 'member')
    cancellationActor = 'the member';
  if (!adminCoachId && initiatedBy === 'coach') cancellationActor = 'the coach';

  useEffect(() => {
    if (!lifecycle.confirmedReceipt || confirmation) return;
    const { terminalSummary } = lifecycle.confirmedReceipt;
    setConfirmation({
      cancelled:
        terminalSummary?.affectedCount ??
        (isSeries
          ? authoritativeTailAppointments.length || seriesOption?.count || 1
          : 1),
      windowEnd:
        terminalSummary?.windowEnd ??
        (isSeries ? seriesPreviewWindowEnd : session.endedAt),
      windowStart:
        terminalSummary?.windowStart ??
        (isSeries ? seriesPreviewWindowStart : session.startedAt),
    });
  }, [
    authoritativeTailAppointments.length,
    confirmation,
    isSeries,
    lifecycle.confirmedReceipt,
    seriesOption?.count,
    seriesPreviewWindowEnd,
    seriesPreviewWindowStart,
    session.endedAt,
    session.startedAt,
  ]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const retryRestoredSubmitting =
      (event.nativeEvent as SubmitEvent).submitter?.getAttribute(
        'data-retry-restored-submitting',
      ) === 'true';
    if (
      pendingOperationId ||
      submitInFlightRef.current ||
      operationReviewRequired ||
      upgradeRequired ||
      nativeRouteBlocked ||
      adminRejected
    )
      return;
    // The in-flight series blocks only a series cancel. It is now prefetched
    // at dialog open, so without the scope check a single-occurrence cancel
    // would be held back by occurrences its submission never reads.
    if (
      nativeTailReviewUnavailable ||
      (isSeries && nativeSeriesAppointments.isFetching)
    )
      return;
    if (!adminAppointment && reasonRequired && !opsNote.trim()) return;
    if (adminSessionNoteRequired && !opsNote.trim()) return;
    if (
      adminAppointment &&
      (adminMemberCollision ||
        (!adminAmbiguous &&
          ((adminAppointment.actionCapabilities.cancel.reasonRequired &&
            !reason.trim()) ||
            (adminAppointment.actionCapabilities.cancel.initiatorRequired &&
              !initiatedBy) ||
            (adminAppointment.actionCapabilities.cancel.opsNoteRequired &&
              !opsNote.trim()))))
    )
      return;
    if ((!session.isChemistryCall || coachNativeAppointment) && !initiatedBy)
      return;
    submitInFlightRef.current = true;
    // Only reachable when adminAppointment is null, so initiatedBy is
    // constrained to the Member/Coach SESSION_INITIATOR_OPTIONS values.
    const resolvedInitiator = (initiatedBy ||
      'coach') as CoachCancelSessionInitiator;
    try {
      setError('');
      setPendingOperationId(null);
      setPendingOperationRetryAfterSeconds(null);
      let response: CoachCancelSessionResponse;
      if (nativeAppointmentId) {
        if (adminAppointment) {
          const adminResult = await adminLifecycle.submit();
          submitInFlightRef.current = false;
          if (!adminResult || adminResult.status === 'pending') return;
          if (adminResult.status === 'rejected') {
            setError(
              adminResult.rejection?.message ??
                'We could not cancel this session. Please try again.',
            );
            return;
          }
          setConfirmation({
            cancelled: adminResult.appointments.length || 1,
          });
          return;
        }
        const preflight = await lifecycle.read();
        if (preflight.kind !== 'ready') {
          submitInFlightRef.current = false;
          if (preflight.kind === 'upgrade') setUpgradeRequired(true);
          else
            setError(
              'Appointment details changed. Review the refreshed session and submit again.',
            );
          return;
        }
        const appointment = preflight.preimage;
        const refreshedStartAt = appointment.currentSchedule?.startAt;
        if (typeof refreshedStartAt !== 'string') {
          submitInFlightRef.current = false;
          setError(
            'Authoritative appointment timing is unavailable. Nothing was submitted.',
          );
          return;
        }
        const preflightTime = Date.now();
        const scheduleChanged = refreshedStartAt !== nativeAppointmentStartAt;
        const nearTermAcknowledged = !scheduleChanged && confirmedWithin48Hours;
        setAuthoritativeStartAt(refreshedStartAt);
        setCurrentTime(preflightTime);
        if (scheduleChanged) setConfirmedWithin48Hours(false);
        if (Date.parse(refreshedStartAt) <= preflightTime) {
          submitInFlightRef.current = false;
          setError(
            'This appointment has already started. Nothing was submitted.',
          );
          return;
        }
        const expectedVersion = appointment.version;
        const command = {
          appointmentId: nativeAppointmentId,
          contractVersion: 'appointment-coach-cancel-browser.v2' as const,
          expectedVersion,
          // Only reachable when adminAppointment is null (coachNativeAppointment
          // is exclusive of adminAppointment), so initiatedBy is constrained to
          // the Member/Coach SESSION_INITIATOR_OPTIONS values.
          initiator: initiatedBy as CoachCancelSessionInitiator,
          nearTermAcknowledged,
          opsNote: opsNote.trim() || null,
          replacementAppointmentId: null,
          scope:
            scope === 'this_and_following' && hasSeriesOption
              ? ('this_and_following' as const)
              : ('appointment' as const),
          ...appointmentSeriesCommandFields(appointmentCapability, appointment),
        };
        const bytes = canonicalAppointmentCommand(command);
        if (lifecycle.submissionBlocked(bytes)) {
          submitInFlightRef.current = false;
          setError(
            'This rejected cancellation is unchanged. Review the refreshed session and change the request before submitting again.',
          );
          return;
        }
        const submission = lifecycle.submissionFor(
          bytes,
          retryRestoredSubmitting,
        );
        if (submission.kind === 'review') {
          submitInFlightRef.current = false;
          setError(
            submission.reason === 'changed-command'
              ? 'The changed request cannot replace the saved cancellation. Restore the original details or contact support.'
              : 'The previous cancellation outcome is unknown. Use the explicit unchanged retry or contact support.',
          );
          return;
        }
        const { idempotencyKey } = submission;
        if (!lifecycle.recordSubmitting({ bytes, idempotencyKey })) {
          submitInFlightRef.current = false;
          setError(
            'We could not safely save this cancellation. Please enable session storage and try again.',
          );
          return;
        }
        const participantCommand =
          command.scope === 'this_and_following'
            ? {
                appointmentId: command.appointmentId,
                expectedSeriesMutationVersion:
                  command.expectedSeriesMutationVersion!,
                expectedVersion: command.expectedVersion,
                idempotencyKey,
                initiator: command.initiator,
                nearTermAcknowledged: command.nearTermAcknowledged,
                opsNote: command.opsNote,
                reason: null,
                replacementAppointmentId: command.replacementAppointmentId,
                scope: command.scope,
                seriesId: command.seriesId!,
              }
            : {
                appointmentId: command.appointmentId,
                expectedSeriesMutationVersion: null,
                expectedVersion: command.expectedVersion,
                idempotencyKey,
                initiator: command.initiator,
                nearTermAcknowledged: command.nearTermAcknowledged,
                opsNote: command.opsNote,
                reason: null,
                replacementAppointmentId: command.replacementAppointmentId,
                scope: command.scope,
                seriesId: null,
              };
        const result = useDirectParticipantTransport
          ? await cancelParticipantAppointment(participantCommand).unwrap()
          : await cancelAppointment({
              ...command,
              idempotencyKey,
              userId: currentUser.id,
            }).unwrap();
        if (result.status === 'pending') {
          setUpgradeRequired(false);
          lifecycle.recordPending({
            bytes,
            idempotencyKey,
            operationId: result.operationId,
            retryAfterSeconds: result.retryAfterSeconds ?? null,
          });
          setPendingOperationId(result.operationId);
          setPendingOperationRetryAfterSeconds(
            result.retryAfterSeconds ?? null,
          );
          submitInFlightRef.current = false;
          return;
        }
        if (result.status === 'rejected') {
          const refreshed = await rereadAfterRejection({
            bytes,
            idempotencyKey,
            operationId: result.operationId,
          });
          const rejectedStartAt = refreshed?.currentSchedule?.startAt;
          if (typeof rejectedStartAt === 'string') {
            setAuthoritativeStartAt(rejectedStartAt);
            setCurrentTime(Date.now());
            setConfirmedWithin48Hours(false);
          }
          setError(
            ('rejection' in result ? result.rejection?.message : undefined) ??
              'We could not cancel this session. Please try again.',
          );
          submitInFlightRef.current = false;
          return;
        }
        if (result.status === 'needs_reconciliation') {
          lifecycle.recordNeedsReconciliation({
            bytes,
            idempotencyKey,
            operationId: result.operationId,
          });
          setOperationReviewRequired(true);
          setError(
            'The provider outcome requires reconciliation. This cancellation remains saved for Mento operations review.',
          );
          submitInFlightRef.current = false;
          return;
        }
        response = cancellationResponseFromAppointments(result.appointments);
      } else if (!adminCoachId) {
        response = await coachCancelSession({
          id: session.id,
          initiatedBy: resolvedInitiator,
          opsNote: opsNote.trim() || undefined,
          scope,
          timezone: timeZone,
        }).unwrap();
      } else if (session.isChemistryCall) {
        response = await adminChemistryCancelSession({
          coachId: adminCoachId,
          confirmedWithin48Hours,
          id: session.id,
          memberId: session.userId,
          reason: opsNote.trim(),
        }).unwrap();
      } else {
        response = await adminCancelSession({
          coachId: adminCoachId,
          id: session.id,
          initiatedBy: resolvedInitiator,
          memberId: session.userId,
          opsNote: opsNote.trim(),
          scope,
        }).unwrap();
      }
      if (nativeAppointmentId) {
        recordConfirmed({
          retainReceipt: true,
          terminalSummary: terminalSummaryFromResponse(response),
        });
      }
      submitInFlightRef.current = false;
      // See above: a failed acknowledgement receipt is not a failed cancellation.
      setConfirmation(response);
    } catch (cause) {
      const upgradeGuardSpent = appointmentUpgradeGuardSpent(cause);
      const upgradeRetainsOwnership =
        upgradeGuardSpent ||
        (cause as { upgrade?: { state?: unknown } }).upgrade?.state ===
          'reload-requested';
      if (upgradeGuardSpent) setUpgradeRequired(true);
      if (
        nativeAppointmentId &&
        !upgradeRetainsOwnership &&
        !(useDirectParticipantTransport
          ? shouldReconcileParticipantAppointmentWrite(cause)
          : shouldReconcileAppointmentWrite(cause))
      ) {
        clearOwnedOperation();
      }
      submitInFlightRef.current = false;
      setError(cancelFailureMessage(cause));
    }
  };

  const handleDone = () => {
    // Best-effort receipt cleanup. This used to block closing when the receipt
    // could not be written or removed, which trapped Ops in the dialog after a
    // completed cancellation: while storage is unavailable
    // removeStoredLifecycleRecord always fails, so no number of clicks could
    // ever succeed. The cancellation is already durable server-side, so
    // acknowledging it must always close.
    if (nativeAppointmentId && confirmation && !lifecycle.confirmedReceipt) {
      recordConfirmed({
        retainReceipt: true,
        terminalSummary: terminalSummaryFromResponse(confirmation),
      });
    }
    consumeConfirmedReceipt();
    close();
  };

  return (
    <Drawer
      anchor="right"
      PaperProps={{
        'aria-labelledby': 'coach-cancel-session-title',
        'aria-modal': true,
        role: 'dialog',
      }}
      open
      onClose={requestClose}
    >
      <div
        className="flex h-full max-w-full flex-col"
        data-action-source={actionSubject?.source}
        data-testid="coach-cancel-drawer"
        style={{ width: 532 }}
      >
        <header className="flex shrink-0 items-center justify-between px-[30px] pb-1 pt-8">
          <Typography id="coach-cancel-session-title" variant="h4">
            {confirmation ? 'Confirmed!' : 'Cancel'}
          </Typography>
          <IconButton
            aria-label="Close"
            className="p-1"
            disabled={Boolean(pendingOperationId)}
            onClick={requestClose}
          >
            <Icon>close</Icon>
          </IconButton>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto px-[30px] pb-4 pt-1">
          {confirmation ? (
            <div className="flex flex-col gap-3">
              {error && <Alert severity="error">{error}</Alert>}
              {nativeRouteBlocked && (
                <Alert severity="error">
                  Appointment cancellation is temporarily unavailable.
                </Alert>
              )}
              <CoachSessionParticipants session={session} />
              <div className="rounded-lg border border-cement-200 px-3 py-4 text-cement-400 line-through">
                {formatSeriesWindow(
                  seriesConfirmationWindowStart,
                  seriesConfirmationWindowEnd,
                  timeZone,
                )}
              </div>
              <Typography variant="body2">
                {confirmedAffectedCount
                  ? sessionCountLabel(confirmedAffectedCount)
                  : 'Future sessions in this series'}
              </Typography>
              <div aria-atomic="true" aria-live="polite" role="status">
                <Typography variant="body1">
                  {`${isSeries ? 'have' : 'has'} been cancelled by ${cancellationActor}.`}
                </Typography>
              </div>
              {opsNote.trim() && (
                <div className="mt-8">
                  <Typography>Cancellation note added:</Typography>
                  <Typography className="italic">{opsNote.trim()}</Typography>
                </div>
              )}
            </div>
          ) : (
            <form
              className="mt-1 flex flex-col gap-4"
              id="coach-cancel-session-form"
              onSubmit={handleSubmit}
            >
              {pendingOperationId && (
                <Alert severity="info">
                  {appointmentPendingMessage('cancellation')}
                </Alert>
              )}
              {upgradeRequired && (
                <Alert severity="error">
                  Appointment client upgrade required. This operation remains
                  saved for review; reload or contact support before continuing.
                </Alert>
              )}
              {error && <Alert severity="error">{error}</Alert>}
              {adminOptionsError && (
                <Alert severity="error">{adminOptionsError}</Alert>
              )}
              {nativeTailReviewUnavailable && (
                <Alert severity="error">
                  Authoritative future sessions are unavailable. Nothing can be
                  submitted until the series is refreshed.
                </Alert>
              )}
              {adminMemberCollision && (
                <Alert severity="error">
                  An admin cannot cancel an Appointment where they are the
                  selected member.
                </Alert>
              )}
              {adminLifecycle.terminalHydrationForbidden && (
                <Alert severity="warning">
                  {PLATFORM_ADMIN_TERMINAL_HYDRATION_FORBIDDEN_MESSAGE}
                </Alert>
              )}
              {!adminLifecycle.terminalHydrationForbidden &&
                adminLifecycle.record?.phase === 'pending' && (
                  <Alert severity="info">
                    The admin cancellation is still pending.
                  </Alert>
                )}
              {adminLifecycle.record?.phase === 'ambiguous' && (
                <Alert severity="warning">
                  The previous admin cancellation outcome is unknown. Retry uses
                  the exact saved request.
                </Alert>
              )}
              {adminManualReview && (
                <Alert severity="warning">
                  {PLATFORM_ADMIN_MANUAL_REVIEW_MESSAGE}
                </Alert>
              )}
              {lifecycle.reviewed && (
                <Alert severity="warning">
                  Refreshed appointment state: version{' '}
                  {lifecycle.reviewed.version}, series{' '}
                  {lifecycle.reviewed.seriesId ?? 'standalone'}, fence{' '}
                  {lifecycle.reviewed.seriesMutationVersion}.
                </Alert>
              )}
              {lifecycle.reviewUnavailable && (
                <Alert severity="warning">
                  Authoritative appointment state is unavailable. Nothing was
                  submitted; review and reconfirm to try another detail read.
                </Alert>
              )}
              {lifecycle.rejected && (
                <Alert severity="warning">
                  Rejected operation {lifecycle.rejected.operationId} is
                  retained for review.
                </Alert>
              )}
              {restoredSubmittingNeedsRetry && (
                <Alert severity="info">
                  {/* The Confirm/retry control is rendered unconditionally in
                      this dialog's footer, so a retry is always available. */}
                  {appointmentRestoredSubmittingMessage('cancellation', {
                    retryAvailable: true,
                  })}
                </Alert>
              )}
              {hasSeriesOption && !session.isChemistryCall && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSeries}
                      disabled={actionControlsFrozen}
                      onChange={(event) => {
                        if (actionControlsFrozen) return;
                        setScope(
                          event.target.checked
                            ? 'this_and_following'
                            : 'single',
                        );
                      }}
                    />
                  }
                  label="Apply to all future sessions in this series"
                />
              )}
              <CoachSessionParticipants session={session} />
              {isSeries ? (
                <div
                  className="rounded-lg border border-cement-200 px-3 py-4 text-cement-400 line-through"
                  data-testid="cancel-series-context"
                >
                  <Typography color="textSecondary" variant="body2">
                    {seriesScopeSummary}
                  </Typography>
                </div>
              ) : (
                <div
                  className="rounded-lg border border-cement-200 px-3 py-4"
                  data-testid="cancel-single-context"
                >
                  <Typography
                    className="line-through"
                    color="textSecondary"
                    variant="body2"
                  >
                    {`${sessionCountLabel(singleOption?.count ?? 1)} • ${formatIsoDateTimeRange(
                      session.startedAt,
                      session.endedAt,
                      timeZone,
                    )}`}
                  </Typography>
                </div>
              )}

              {((adminAppointment &&
                adminAppointment.actionCapabilities.cancel.initiatorRequired) ||
                !session.isChemistryCall ||
                coachNativeAppointment) && (
                <div>
                  <Typography variant="subtitle2">
                    Who initiated cancellation?
                  </Typography>
                  <RadioGroup
                    aria-label="Who initiated cancellation?"
                    className="flex-row"
                    value={initiatedBy}
                    onChange={(event) => {
                      if (actionControlsFrozen) return;
                      setInitiatedBy(
                        event.target.value as AppointmentCancellationInitiator,
                      );
                    }}
                  >
                    {initiatorOptions.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        control={<Radio />}
                        disabled={actionControlsFrozen}
                        label={option.label}
                        value={option.value}
                      />
                    ))}
                  </RadioGroup>
                </div>
              )}

              {adminAppointment?.actionCapabilities.cancel.reasonRequired && (
                <TextField
                  disabled={actionControlsFrozen}
                  label="Cancellation reason"
                  minRows={1}
                  value={reason}
                  fullWidth
                  multiline
                  required
                  onChange={(event) => {
                    if (!actionControlsFrozen) setReason(event.target.value);
                  }}
                />
              )}
              <TextField
                disabled={actionControlsFrozen}
                label={opsNoteLabel}
                minRows={1}
                required={
                  adminAppointment
                    ? adminAppointment.actionCapabilities.cancel.opsNoteRequired
                    : reasonRequired || adminSessionNoteRequired
                }
                value={opsNote}
                fullWidth
                multiline
                onChange={(event) => {
                  if (!actionControlsFrozen) setOpsNote(event.target.value);
                }}
              />
              {session.isChemistryCall &&
                adminCoachId &&
                !nativeAppointmentId && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirmedWithin48Hours}
                        disabled={actionControlsFrozen}
                        onChange={(event) => {
                          if (!actionControlsFrozen)
                            setConfirmedWithin48Hours(event.target.checked);
                        }}
                      />
                    }
                    label="Confirmed cancellation within 48 hours"
                  />
                )}
            </form>
          )}
        </main>
        <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-cement-200 px-[14px] py-4">
          {confirmation ? (
            <Button variant="contained" onClick={handleDone}>
              OK
            </Button>
          ) : (
            <>
              <Button
                disabled={isLoading || Boolean(pendingOperationId)}
                variant="text"
                onClick={requestClose}
              >
                Cancel
              </Button>
              <Button
                data-retry-restored-submitting={
                  restoredSubmittingNeedsRetry ? 'true' : undefined
                }
                disabled={
                  isLoading ||
                  nativeRouteBlocked ||
                  Boolean(adminOptionsError) ||
                  lifecycle.isReading ||
                  operationReviewRequired ||
                  nativeTailReviewUnavailable ||
                  (isSeries && nativeSeriesAppointments.isFetching) ||
                  upgradeRequired ||
                  adminManualReview ||
                  adminRejected ||
                  Boolean(pendingOperationId) ||
                  adminMemberCollision ||
                  (!adminAppointment && reasonRequired && !opsNote.trim()) ||
                  (adminSessionNoteRequired && !opsNote.trim()) ||
                  (!adminAmbiguous &&
                    ((adminAppointment?.actionCapabilities.cancel
                      .reasonRequired &&
                      !reason.trim()) ||
                      (adminAppointment?.actionCapabilities.cancel
                        .opsNoteRequired &&
                        !opsNote.trim()) ||
                      (adminAppointment?.actionCapabilities.cancel
                        .initiatorRequired &&
                        !initiatedBy))) ||
                  (!session.isChemistryCall &&
                    !nativeAppointmentId &&
                    !initiatedBy) ||
                  (coachNativeAppointment && !initiatedBy)
                }
                form="coach-cancel-session-form"
                type="submit"
                variant="contained"
              >
                {restoredSubmittingNeedsRetry || adminAmbiguous
                  ? APPOINTMENT_RETRY_BUTTON_LABEL
                  : 'Confirm'}
              </Button>
              {adminRejected && (
                <Button variant="outlined" onClick={startNewAdminAttempt}>
                  Start new attempt
                </Button>
              )}
            </>
          )}
        </footer>
      </div>
    </Drawer>
  );
};

export default CoachCancelSessionDialog;
